import cn from 'classnames';
import { isNil } from 'lodash';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Icon } from '../../Icon';
import { FIELD_HEIGHT } from './fieldHeight';
import { InputErrorMessage } from './InputErrorMessage';
import { OptionalTag } from './OptionalTag';

export interface InputRef {
  focus: () => void;
  blur: () => void;
}

interface IBaseInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'placeholder' | 'type'> {
  errorMessage?: string;
  hasError?: boolean;
  label: string;
  name: string;
  type?: 'text' | 'number' | 'email' | 'password' | 'search';
  isOptional?: boolean;
  /** Optional badge/icon rendered inside the input on the left; shifts the label and text to make room. */
  leadingIcon?: React.ReactNode;
  /** Renders an alert icon before the error message. */
  showErrorIcon?: boolean;
  onClear?: () => void;
}

/**
 * @param props.errorMessage - The error message to display
 * @param props.hasError - Whether the input has an error
 * @param props.label - The label of the input
 * @param props.name - The name of the input
 * @param props.onChange - Callback sending string | number | undefined | null value to parent
 * @param props.type - The type of the input (text, number, email, password)
 * @param props.value - The value of the input
 * @param props.restProps - The rest of the props of type React.InputHTMLAttributes<HTMLInputElement>
 * @param props.isOptional - Will render `(optional)` after the label if true
 *
 * @returns A Design System Input component for text, number, email, and password inputs with
 *          floating label and error state display.
 */
export const BaseInput = React.forwardRef<InputRef, IBaseInputProps>(
  (
    {
      label,
      value,
      name,
      onChange,
      className,
      onFocus,
      onBlur,
      type = 'text',
      hasError,
      isOptional = false,
      errorMessage,
      leadingIcon,
      showErrorIcon = false,
      onClear,
      ...restProps
    },
    forwardedRef
  ) => {
    const [labelIsFloating, setLabelIsFloating] = useState<boolean>(!!value);
    const [passwordIsRevealed, setPasswordIsRevealed] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const isPassword = type === 'password';
    const isShowingErrorMessage = !!errorMessage && !!hasError;
    const isShowingClearButton = !!onClear && !isPassword && !isNil(value) && value !== '';
    const errorMessageId = `${name}-error`;

    useImperativeHandle(
      forwardedRef,
      () => ({
        focus: () => {
          inputRef.current?.focus();
        },
        blur: () => {
          inputRef.current?.blur();
        },
      }),
      []
    );

    useEffect(() => {
      if (value) {
        setLabelIsFloating(true);
      } else if (document.activeElement !== inputRef.current) {
        setLabelIsFloating(false);
      }
    }, [inputRef, value]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setLabelIsFloating(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setLabelIsFloating(!!value);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
    };

    const handleClear = () => {
      onClear?.();
      inputRef.current?.focus();
    };

    return (
      <div className='relative bg-none'>
        <div className='relative'>
          <input
            ref={inputRef}
            className={cn(className, FIELD_HEIGHT, 'w-full rounded-xl border-2 bg-milk outline-none', {
              'px-4': !leadingIcon,
              'pl-12 pr-4': !!leadingIcon,
              'pr-12': isPassword || isShowingClearButton,
              'border-error text-error': hasError,
              'border-neutral-50 text-eggplant focus-visible:border-eggplant-500': !hasError,
              'pt-4': !!label,
            })}
            type={isPassword && passwordIsRevealed ? 'text' : type}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            name={name}
            id={name}
            aria-invalid={hasError ? true : undefined}
            aria-describedby={isShowingErrorMessage ? errorMessageId : undefined}
            {...restProps}
          />
          {leadingIcon && <span className='absolute left-4 top-1/2 -translate-y-1/2'>{leadingIcon}</span>}
          <label
            htmlFor={name}
            className={cn('absolute text-eggplant-400 transition-all', {
              'left-[18px]': !leadingIcon,
              'left-12': !!leadingIcon,
              'top-4 text-body-400 leading-tight': !labelIsFloating,
              'top-2.5 text-label-400': labelIsFloating,
            })}>
            {label}
            <OptionalTag isOptional={isOptional} isFloating={labelIsFloating} />
          </label>
          {isPassword && (
            <button
              type='button'
              onClick={() => setPasswordIsRevealed((revealed) => !revealed)}
              aria-label={passwordIsRevealed ? 'Hide password' : 'Show password'}
              className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer border-none bg-transparent p-0 text-eggplant-400'>
              <Icon name={passwordIsRevealed ? 'eyeOpen' : 'eyeClosed'} size={16} />
            </button>
          )}
          {isShowingClearButton && (
            <button
              type='button'
              aria-label={`Clear ${label}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleClear}
              className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer border-none bg-transparent p-0 text-eggplant-400'>
              <Icon name='close' size={16} />
            </button>
          )}
        </div>
        {isShowingErrorMessage && (
          <InputErrorMessage id={errorMessageId} message={errorMessage} showIcon={showErrorIcon} className='mt-2' />
        )}
      </div>
    );
  }
);

interface ITextInputProps extends Omit<IBaseInputProps, 'onChange' | 'className'> {
  onChange: (value: string) => void;
}

export const Input = React.forwardRef<InputRef, ITextInputProps>(({ onChange, ...restProps }, forwardedRef) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value ?? '');
  };
  return <BaseInput {...restProps} onChange={handleChange} ref={forwardedRef} />;
});
